import { nameNormalized } from "@app/shared/utils/tableSortingFns";
import { normalizeString } from "@app/utils/string";
import { ProgressBar } from "@design-system/DataDisplay/ProgressBar/ProgressBar";
import { StatusTagCircular } from "@design-system/DataDisplay/StatusTagCircular";
import { Tooltip } from "@design-system/DataDisplay/Tooltip";
import { Icon } from "@design-system/Icon";
import { Button } from "@design-system/Inputs/Button";
import { Box } from "@design-system/Layout/Box";
import { FlexCol, FlexRow } from "@design-system/Layout/Flex";
import { compareBasic, type ColumnDef } from "@design-system/Table";
import { Ellipsis } from "@design-system/Typography/Ellipsis";
import { Text } from "@design-system/Typography/Text";
import dayjs from "dayjs";
import LocalizedFormat from "dayjs/plugin/localizedFormat";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { AssigneesUserAvatar } from "./components/AssigneesUserAvatar";
import { EditOrDeleteDropdown } from "./components/EditOrDeleteDropdown";
import { keys, RESOURCE_NAME } from "./i18n";
import { getQuestionnaireProgress, getQuestionnaireStatus } from "./services";
import { type ExternalQuestionnairesListItem } from "./types";

export const columns: ColumnDef<ExternalQuestionnairesListItem>[] = [
    {
        id: "name",
        accessorKey: "name",
        sortingFn: nameNormalized,
        header: () => {
            const { t } = useTranslation(RESOURCE_NAME);
            return (
                <Text variant="body-sm-bold" className="text-secondary">
                    {t(keys.table_header_name)}
                </Text>
            );
        },
        cell: ({ row }) => {
            return (
                <Link className="[all:unset]" to={row.original.id}>
                    <Text variant="body-bold" className="cursor-pointer">
                        {row.original.name}
                    </Text>
                </Link>
            );
        },
    },
    {
        id: "company",
        accessorKey: "companyName",
        meta: {
            cellClassName: "min-w-[150px]",
            headerClassName: "min-w-[150px]",
        },
        sortingFn: (rowA, rowB) => {
            return compareBasic(
                normalizeString(rowA.original.companyName ?? ""),
                normalizeString(rowB.original.companyName ?? ""),
            );
        },
        header: () => {
            const { t } = useTranslation(RESOURCE_NAME);
            return (
                <Text variant="body-sm-bold" className="text-secondary">
                    {t(keys.table_header_company)}
                </Text>
            );
        },
        cell: ({ row }) => {
            return (
                <FlexCol>
                    <Text>{row.original.companyName}</Text>
                    <Text variant="body-sm" className="text-secondary">
                        {row.original.requesterEmail}
                    </Text>
                </FlexCol>
            );
        },
    },
    {
        id: "status",
        accessorKey: "status",
        meta: {
            cellClassName: "min-w-[100px]",
            headerClassName: "min-w-[100px]",
        },
        sortingFn: (rowA, rowB) => {
            const statusOrder = { todo: 0, inProgress: 1, validated: 2 };
            const statusA = getQuestionnaireStatus(rowA.original);
            const statusB = getQuestionnaireStatus(rowB.original);
            return statusOrder[statusA] - statusOrder[statusB];
        },
        header: () => {
            const { t } = useTranslation(RESOURCE_NAME);
            return (
                <Text variant="body-sm-bold" className="text-secondary">
                    {t(keys.table_header_status)}
                </Text>
            );
        },
        cell: ({ row }) => {
            const questionnaireStatus = getQuestionnaireStatus(row.original);
            const { t } = useTranslation(RESOURCE_NAME);

            const textTooltip = useMemo(() => {
                if (questionnaireStatus === "validated") {
                    return t(keys.tooltip_status_validated);
                }
                if (questionnaireStatus === "inProgress") {
                    return t(keys.tooltip_status_in_progress);
                }
                return t(keys.tooltip_status_not_started);
            }, [questionnaireStatus]);
            return (
                <Tooltip>
                    <Tooltip.Trigger>
                        <StatusTagCircular
                            status={questionnaireStatus}
                            size="sm"
                        />
                    </Tooltip.Trigger>
                    <Tooltip.Content>{textTooltip}</Tooltip.Content>
                </Tooltip>
            );
        },
    },
    {
        id: "progress",
        accessorKey: "progress",
        meta: {
            cellClassName: "min-w-[200px]",
            headerClassName: "min-w-[200px]",
            colWidth: "200px",
        },
        sortingFn: (rowA, rowB) => {
            const { completedQuestions, totalQuestions } =
                getQuestionnaireProgress(rowA.original);
            const {
                completedQuestions: completedQuestionsB,
                totalQuestions: totalQuestionsB,
            } = getQuestionnaireProgress(rowB.original);
            return compareBasic(
                completedQuestions / totalQuestions,
                completedQuestionsB / totalQuestionsB,
            );
        },
        header: () => {
            const { t } = useTranslation(RESOURCE_NAME);
            return (
                <Text variant="body-sm-bold" className="text-secondary">
                    {t(keys.table_header_progress)}
                </Text>
            );
        },
        cell: ({ row }) => {
            const { completedQuestions, totalQuestions } =
                getQuestionnaireProgress(row.original);

            if (totalQuestions === 0) {
                return "";
            }
            return (
                <FlexRow gap="5" w="full">
                    <ProgressBar
                        segments={[
                            {
                                value: completedQuestions,
                                labelValue: `${((completedQuestions || 0) / (totalQuestions || 0)) * 100}%`,
                                label: "N/A",
                            },
                        ]}
                        total={totalQuestions}
                        noDataPlaceholder="N/A"
                    />
                    <Text>{`${completedQuestions} / ${totalQuestions}`}</Text>
                </FlexRow>
            );
        },
    },
    {
        id: "deadline",
        accessorKey: "deadline",
        header: () => {
            const { t } = useTranslation(RESOURCE_NAME);
            return (
                <Text variant="body-sm-bold" className="text-secondary">
                    {t(keys.table_header_deadline)}
                </Text>
            );
        },
        sortingFn: (rowA, rowB) => {
            return compareBasic(
                new Date(rowA.original.deadline),
                new Date(rowB.original.deadline),
            );
        },
        cell: ({ row }) => {
            dayjs.extend(LocalizedFormat);

            const displayDate = row.original.deadline
                ? dayjs(row.original.deadline).format("LL")
                : "";

            return (
                <FlexRow gap="2" alignItems="center">
                    {row.original.deadline && (
                        <Icon
                            name="calendar"
                            size="sm"
                            className="text-secondary"
                        />
                    )}
                    <Ellipsis asChild hasTooltip>
                        <Text>{displayDate}</Text>
                    </Ellipsis>
                </FlexRow>
            );
        },
    },
    {
        id: "assignees",
        meta: {
            cellClassName: "min-w-[120px]",
            headerClassName: "min-w-[120px]",
        },
        header: () => {
            const { t } = useTranslation(RESOURCE_NAME);
            return (
                <Text variant="body-sm-bold" className="text-secondary">
                    {t(keys.table_header_assignees)}
                </Text>
            );
        },
        cell: ({ row }) => {
            const assigneeIds: Set<string> = new Set(
                row.original.questions
                    .map((q) => q.ownerId)
                    .filter((id) => id != null),
            );

            return <AssigneesUserAvatar userIds={assigneeIds} />;
        },
    },
    {
        id: "updatedAt",
        accessorKey: "updatedAt",
        meta: {
            cellClassName: "min-w-[140px]",
            headerClassName: "min-w-[140px]",
        },
        header: () => {
            const { t } = useTranslation(RESOURCE_NAME);
            return (
                <Text variant="body-sm-bold" className="text-secondary">
                    {t(keys.table_header_last_modified)}
                </Text>
            );
        },
        cell: ({ row }) => dayjs(row.original.updatedAt).format("LL"),
    },
    {
        id: "actions",
        cell: ({ row }) => (
            <Box display="grid" className="place-items-center min-w">
                <EditOrDeleteDropdown questionnaireId={row.original.id}>
                    <Button size="sm" variant="outlined">
                        <Button.Icon name="more" />
                    </Button>
                </EditOrDeleteDropdown>
            </Box>
        ),
        maxSize: 80,
        meta: {
            cellClassName: "min-w-[72px] max-w-[72px]",
            headerClassName: "min-w-[72px] max-w-[72px]",
            colWidth: "72px",
        },
    },
];
